import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SpeakerHigh, Pause, Heart, CaretRight } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

/**
 * TrendingVoiceDropsStrip — horizontally-scrolling row of the hottest posts
 * with a Voice Drop attached (Robot, KTV & friends). Tap the speaker to hear
 * the drop inline; tap the card to open the post. Auto-hides when empty.
 */
export default function TrendingVoiceDropsStrip() {
  const [drops, setDrops] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/posts/voice-drops/trending", { params: { limit: 10 } });
        if (alive) setDrops(data || []);
      } catch (e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  const togglePlay = (e, post) => {
    e.stopPropagation();
    if (playingId === post.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(fileUrl(post.voice_drop_path));
    a.onended = () => setPlayingId(null);
    a.play().catch(() => {});
    audioRef.current = a;
    setPlayingId(post.id);
  };

  if (dismissed || drops.length === 0) return null;

  return (
    <div
      className="absolute left-0 right-0 top-[152px] z-20 px-4 pointer-events-none"
      data-testid="trending-voicedrops-strip"
    >
      <div className="pointer-events-auto">
        <div className="flex items-center justify-between mb-1.5 px-1">
          <p className="text-sky-300 text-[9px] tracking-[0.35em] uppercase font-black flex items-center gap-1.5">
            <SpeakerHigh size={11} weight="fill" /> Trending Voice Drops
          </p>
          <button
            onClick={() => setDismissed(true)}
            className="text-cream/40 hover:text-cream/70 text-[9px] tracking-[0.3em] uppercase font-bold"
            data-testid="trending-voicedrops-dismiss"
          >Hide</button>
        </div>
        <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1">
          {drops.map((p) => (
            <div
              key={p.id}
              onClick={() => nav(`/p/${p.id}`)}
              className="flex-shrink-0 snap-start w-[200px] rounded-2xl crystal-glass px-3 py-2 hover:border-sky-400/40 transition-colors cursor-pointer"
              data-testid={`voicedrop-card-${p.id}`}
            >
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => togglePlay(e, p)}
                  data-testid={`voicedrop-play-${p.id}`}
                  className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    playingId === p.id ? "bg-sky-400 text-ink animate-pulse" : "bg-sky-400/20 text-sky-300"
                  }`}
                >
                  {playingId === p.id ? <Pause size={16} weight="fill" /> : <SpeakerHigh size={16} weight="fill" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-cream font-bold text-xs truncate leading-tight">
                    @{p.author?.username || "dancer"}
                  </p>
                  <p className="text-cream/50 text-[10px] truncate">
                    {p.caption || "Voice Drop"}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-cream/60 text-[9px] tracking-[0.25em] uppercase font-bold flex items-center gap-1">
                  <Heart size={10} weight="fill" /> {p.likes_count || 0}
                </span>
                <span className="text-sky-300 text-[9px] tracking-[0.25em] uppercase font-black flex items-center gap-0.5">
                  {p.voice_drop_duration ? `${Math.round(p.voice_drop_duration)}s` : "Play"} <CaretRight size={9} weight="bold" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
